import { useState, useEffect } from 'react';
import { useParams, useNavigate, Link } from 'react-router-dom';
import { useCourseStore } from '../../../context/CourseStoreContext';
import CourseBasicForm from './components/CourseBasicForm';
import LessonManager from './components/LessonManager';
import QuizEditor from './components/QuizEditor';
import { createCourse, getCourseById, updateCourseApi, updateFinalCourseQuizApi } from '../../../api/apiServices';
import SectionManager from './components/SectionManager';



function emptyCourse() {
  return {
    title: '',
    description: '',
    longDescription: '',
    thumbnail: '',
    category: '',
    instructor: '',
    instructorAvatar: '',
    instructorBio: '',
    duration: '',
    level: 'Beginner',
    price: 0,
    tags: [],
    whatYouLearn: [],
    requirements: [],
    finalQuiz: { 
      title: 'Final Course Quiz', 
      questions: [], 
      passingScore: 70 
    },
    sections: [],
    isPublished: false,
    studentsEnrolled: 0,
  };
}

export default function CourseEditorPage() {
  const { id } = useParams();
  const navigate = useNavigate();
  const isNew = !id;

  const [course, setCourse] = useState(emptyCourse);
  const [thumbnailFile, setThumbnailFile] = useState(null);
  const [instructorAvatarFile, setInstructorAvatarFile] = useState(null);
  const [activeTab, setActiveTab] = useState('info');
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [saved, setSaved] = useState(false);
  const [error, setError] = useState('');
  const [createdId, setCreatedId] = useState(null);

  // Fetch course when editing
  useEffect(() => {
    if (id) {
      fetchCourse();
    } else {
      setCourse(emptyCourse());
      setThumbnailFile(null);
      setInstructorAvatarFile(null);
    }
  }, [id]);

  // Handle sections change from SectionManager
  const handleSectionsChange = (newSections) => {
    setCourse(prev => ({ ...prev, sections: newSections }));
  };

  const fetchCourse = async () => {
    setLoading(true);
    setError('');

    try {
      const response = await getCourseById(id);
      
      if (response?.success && response?.course) {
        const fetchedCourse = response?.course;
        
        setCourse({
          _id: fetchedCourse._id,
          title: fetchedCourse.title || '',
          description: fetchedCourse.description || '',
          longDescription: fetchedCourse.longDescription || '',
          thumbnail: fetchedCourse.thumbnail || '',
          category: fetchedCourse.category || '',
          instructor: fetchedCourse.instructor || '',
          instructorAvatar: fetchedCourse.instructorAvatar || '',
          instructorBio: fetchedCourse.instructorBio || '',
          duration: fetchedCourse.duration || '',
          level: fetchedCourse.level || 'Beginner',
          price: fetchedCourse.price || 0,
          tags: fetchedCourse.tags || [],
          whatYouLearn: fetchedCourse.whatYouLearn || [],
          requirements: fetchedCourse.requirements || [],
          sections: fetchedCourse.sections || [],
          finalQuiz: fetchedCourse.finalQuiz || { 
          title: 'Final Course Quiz', 
          questions: [], 
          passingScore: 70 
          },
          isPublished: fetchedCourse.isPublished || false,
          studentsEnrolled: fetchedCourse.studentsEnrolled || 0,
          lessons: fetchedCourse.lessons || [],
        });
      } else {
        setError("Failed to load course data");
      }
    } catch (err) {
      console.error("Fetch course error:", err);
      setError("Could not load course. Please try again.");
    } finally {
      setLoading(false);
    }
  };

  const handleBasicChange = (updates) => {
    setCourse(prev => ({ ...prev, ...updates }));

    // Capture files when they come from CourseBasicForm
    if (updates.thumbnail && updates.thumbnail.startsWith('blob:')) {
      // We already set the file in CourseBasicForm, but we need to keep reference
      // Better to pass files separately from form (we'll adjust CourseBasicForm too)
    }
  };

  const handleSave = async () => {
    if (!course.title?.trim()) {
      setError("Course title is required");
      return;
    }

    setSaving(true);
    setError('');

    try {
      const formData = new FormData();

      // Append all text fields
      formData.append('title', course.title.trim());
      formData.append('description', course.description?.trim() || '');
      formData.append('longDescription', course.longDescription?.trim() || '');
      formData.append('category', course.category || '');
      formData.append('level', course.level || 'Beginner');
      formData.append('price', course.price || 0);
      formData.append('duration', course.duration?.trim() || '');
      formData.append('instructor', course.instructor?.trim() || '');
      formData.append('instructorBio', course.instructorBio?.trim() || '');

      // Append arrays as JSON strings
      formData.append('whatYouLearn', JSON.stringify(course.whatYouLearn || []));
      formData.append('requirements', JSON.stringify(course.requirements || []));
      formData.append('tags', JSON.stringify(course.tags || []));

      // Append files (if selected)
      if (thumbnailFile) {
        formData.append('thumbnail', thumbnailFile);
      }
      if (instructorAvatarFile) {
        formData.append('instructorAvatar', instructorAvatarFile);
      }

      let result;
      if (isNew) {
        result = await createCourse(formData);   // This should now accept FormData
      } else {
        result = await updateCourseApi(id, formData);
      }

      setSaved(true);
      
      if (isNew && result?.course?._id) {
        // Redirect to edit mode with real backend ID
        navigate(`/admin/courses/${result.course._id}/edit`, { replace: true });
      }

      setTimeout(() => setSaved(false), 2000);

    } catch (err) {
      console.error("Save error:", err);
      setError(err.response?.data?.message || "Failed to save course. Please try again.");
    } finally {
      setSaving(false);
    }
  };


  const tabs = [
    { key: 'info', icon: 'ri-information-line', label: 'Basic Info' },
    { key: 'lessons', icon: 'ri-play-list-line', label: `Lessons (${course?.sections?.reduce((sum, sec) => sum + (sec.lectures?.length || 0), 0) || 0})` },
    { key: 'quiz', icon: 'ri-trophy-line', label: 'Final Quiz' },
  ];

  return (
    <div className="min-h-screen bg-slate-50">
      <div className="bg-white border-b border-slate-100 px-8 py-4 flex items-center justify-between sticky top-0 z-20">
        <div className="flex items-center gap-3">
          <Link
            to="/admin/courses"
            className="w-8 h-8 flex items-center justify-center text-slate-400 hover:text-slate-600 hover:bg-slate-100 rounded-lg cursor-pointer"
          >
            <i className="ri-arrow-left-line" />
          </Link>
          <div>
            <div className="flex items-center gap-1.5 text-xs text-slate-400">
              <Link to="/admin/dashboard" className="hover:text-teal-600">Admin</Link>
              <i className="ri-arrow-right-s-line" />
              <Link to="/admin/courses" className="hover:text-teal-600">Courses</Link>
              <i className="ri-arrow-right-s-line" />
              <span className="text-slate-600">{isNew ? 'New Course' : 'Edit Course'}</span>
            </div>
            <h1 className="text-lg font-bold text-slate-900 mt-0.5 leading-none">
              {isNew ? 'Create New Course' : (course.title || 'Edit Course')}
            </h1>
          </div>
        </div>
        

        <div className="flex items-center gap-3">
          {!isNew && (
            <Link
              to={`/courses/${id}`}
              target="_blank"
              className="flex items-center gap-1.5 px-3 py-2 text-sm text-slate-600 border border-slate-200 rounded-lg hover:bg-slate-50 cursor-pointer"
            >
              <i className="ri-external-link-line text-xs" /> Preview
            </Link>
          )}

          <button
            onClick={handleSave}
            disabled={saving || !course.title?.trim()}
            className={`flex items-center gap-2 px-5 py-2 text-sm font-semibold rounded-lg cursor-pointer transition-colors disabled:opacity-50 ${
              saved ? 'bg-emerald-500 text-white' : 'bg-teal-600 hover:bg-teal-700 text-white'
            }`}
          >
            {saving ? (
              <> <i className="ri-loader-4-line animate-spin" /> Saving... </>
            ) : saved ? (
              <> <i className="ri-check-line" /> Saved! </>
            ) : (
              <> <i className="ri-save-line" /> {isNew ? 'Create Course' : 'Save Changes'} </>
            )}
          </button>
        </div>
      </div>

      <div className="bg-white border-b border-slate-100 px-8">
        <div className="flex">
          {tabs.map(tab => (
            <button
              key={tab.key}
              onClick={() => setActiveTab(tab.key)}
              className={`flex items-center gap-2 px-5 py-3.5 text-sm font-medium border-b-2 transition-colors cursor-pointer whitespace-nowrap ${
                activeTab === tab.key
                  ? 'border-teal-600 text-teal-600'
                  : 'border-transparent text-slate-500 hover:text-slate-700'
              }`}
            >
              <div className="w-4 h-4 flex items-center justify-center"><i className={tab.icon} /></div>
              {tab.label}
            </button>
          ))}
        </div>
      </div>

      <div className="p-8">
        {activeTab === 'info' && (
          <CourseBasicForm
            course={course}
            onChange={handleBasicChange}
            onThumbnailChange={setThumbnailFile}
            onInstructorAvatarChange={setInstructorAvatarFile}
            
          />
        )}

        {activeTab === 'lessons' && (
          <SectionManager
            courseId={id || course._id}
            sections={course.sections || []}
            onChange={handleSectionsChange}
          />
        )}

        {activeTab === 'quiz' && (
          <div className="max-w-3xl">
            <div className="bg-amber-50 border border-amber-200 rounded-xl p-4 mb-5 flex items-start gap-3">
              <div className="w-5 h-5 flex items-center justify-center text-amber-500 flex-shrink-0 mt-0.5">
                <i className="ri-trophy-line" />
              </div>
              <div>
                <p className="text-sm font-semibold text-amber-800">Final Course Quiz</p>
                <p className="text-xs text-amber-700 mt-0.5">
                  Students must complete all lessons before attempting this quiz. It&apos;s required for course completion and certification.
                </p>
              </div>
            </div>
            <QuizEditor 
            quiz={course.finalQuiz || { title: 'Final Course Quiz', questions: [] }}
            onChange={async (quizData) => {
              try {
                const payload = {
                  title: quizData.title || "Final Course Quiz",
                  passingScore: quizData.passingScore || 70,
                  questions: (quizData.questions || []).map(q => ({
                    questionText: q.text?.trim() || q.questionText?.trim() || '',
                    options: q.options || [],
                    correctAnswer: Number(q.correctAnswer) ?? 0,
                    explanation: q.explanation?.trim() || ''
                  })).filter(q => q.questionText)
                };
                const result = await updateFinalCourseQuizApi(course._id || id, payload);
                if (result.success) {
                  setCourse(prev => ({ ...prev, finalQuiz: result.quiz }));
                }
              } catch (err) {
                console.error(err);
                alert("Failed to save final quiz");
              }
              }}
              />
          </div>
        )}
      </div>
    </div>
  );
}
